// Enchanting (PLR-6). PURE + isomorphic: the enchantment registry, the slot-level
// formula, the seeded weighted enchant selection (with exclusivity groups), and
// enchant application. Follows Minecraft's model to documented tolerance (D12: no
// quirk-parity) — the shelf-power/base-level maths and enchantability windows are
// faithful; the exact random stream is our own deterministic xoshiro.
import { ENCHANT } from '../config';
import type { Rng } from '../engine/rng';
import type { ItemStack, ItemComponents } from './inventory';

export interface EnchantDef {
  id: string;
  /** Rarity weight in the weighted pick (rarer = lower). */
  weight: number;
  maxLevel: number;
  /** Item categories this can apply to (sword/pickaxe/armor/bow/…). */
  applies: readonly string[];
  /** Enchantments it cannot coexist with (mutual exclusivity group). */
  exclusiveWith: readonly string[];
  /** Per-level cost window `[minCost, maxCost]`; a level `L` uses index `L-1`. */
  costs: ReadonlyArray<readonly [number, number]>;
}

export interface EnchantRegistry {
  byId: ReadonlyMap<string, EnchantDef>;
  list: readonly EnchantDef[];
}

/** Build the registry from validated enchantment data. */
export function buildEnchantRegistry(defs: readonly EnchantDef[]): EnchantRegistry {
  return { byId: new Map(defs.map((d) => [d.id, d])), list: defs };
}

/** Tool-material enchantability (§13): richer materials reach stronger enchants. */
export const ENCHANTABILITY: Record<string, number> = {
  wood: 15, gold: 22, stone: 5, iron: 14, diamond: 10, netherite: 15,
};

/** Enchant category for a tool item (its tool class), or null if not enchantable. */
export function toolCategory(toolClass: string | undefined): string | null {
  return toolClass ?? null;
}

/** Shelf power = bookshelves that count, capped at 15 (PLR-6). */
export function shelfPower(shelves: number): number {
  return Math.max(0, Math.min(ENCHANT.SHELF_MAX, shelves));
}

/**
 * The three enchant-slot levels shown by a table (PLR-6):
 *   base = rand(1,8) + floor(shelves/2) + rand(0,shelves)
 *   slots = [ max(base/3,1), base·2/3+1, max(base, shelves·2) ]
 */
export function slotLevels(shelves: number, rng: Rng): [number, number, number] {
  const s = shelfPower(shelves);
  const base = 1 + rng.nextInt(8) + Math.floor(s / 2) + rng.nextInt(s + 1);
  return [
    Math.max(Math.floor(base / 3), 1),
    Math.floor((base * 2) / 3) + 1,
    Math.max(base, s * 2),
  ];
}

interface WeightedEnchant {
  id: string;
  lvl: number;
  weight: number;
}

/** Applicable enchants (with the highest affordable level) at `modified` power. */
function candidates(reg: EnchantRegistry, category: string, modified: number): WeightedEnchant[] {
  const out: WeightedEnchant[] = [];
  for (const e of reg.list) {
    if (!e.applies.includes(category)) continue;
    for (let lvl = e.maxLevel; lvl >= 1; lvl--) {
      const [lo, hi] = e.costs[lvl - 1] ?? [1, 1];
      if (modified >= lo && modified <= hi) { out.push({ id: e.id, lvl, weight: e.weight }); break; }
    }
  }
  return out;
}

function weightedPick(list: WeightedEnchant[], rng: Rng): WeightedEnchant | null {
  const total = list.reduce((a, e) => a + e.weight, 0);
  if (total <= 0) return null;
  let r = rng.nextInt(total);
  for (const e of list) { if (r < e.weight) return e; r -= e.weight; }
  return list[list.length - 1] ?? null;
}

/**
 * Roll the enchants granted by a slot (PLR-6 selection): raise the slot level by
 * the item's enchantability, pick a weighted enchant, then keep adding compatible
 * ones with a decaying chance — enforcing exclusivity groups throughout.
 */
export function rollEnchants(
  reg: EnchantRegistry,
  category: string,
  enchantability: number,
  slotLevel: number,
  rng: Rng,
): Array<{ id: string; lvl: number }> {
  // Modify the effective level by enchantability (MC: +1 + 2×rand(ench/4+1),
  // then ±15% jitter), so richer materials reach stronger enchants.
  const bonus = 1 + rng.nextInt(Math.floor(enchantability / 4) + 1) + rng.nextInt(Math.floor(enchantability / 4) + 1);
  let modified = Math.max(1, Math.round((slotLevel + bonus) * (1 + (rng.nextInt(31) - 15) / 100)));

  let pool = candidates(reg, category, modified);
  const chosen: Array<{ id: string; lvl: number }> = [];
  const taken = new Set<string>();
  const excluded = new Set<string>();
  const first = weightedPick(pool, rng);
  if (!first) return chosen;
  chosen.push({ id: first.id, lvl: first.lvl });
  taken.add(first.id);
  for (const x of reg.byId.get(first.id)!.exclusiveWith) excluded.add(x);

  // Additional enchants with a decaying probability (halved modified each round).
  while (rng.nextInt(50) <= modified) {
    modified = Math.floor(modified / 2);
    pool = candidates(reg, category, modified).filter((c) => !taken.has(c.id) && !excluded.has(c.id));
    const pick = weightedPick(pool, rng);
    if (!pick) break;
    chosen.push({ id: pick.id, lvl: pick.lvl });
    taken.add(pick.id);
    for (const x of reg.byId.get(pick.id)!.exclusiveWith) excluded.add(x);
  }
  return chosen;
}

/** Set an item's enchants (replaces any existing), returning a new stack. */
export function withEnchants(stack: ItemStack, enchants: ReadonlyArray<{ id: string; lvl: number }>): ItemStack {
  const components: ItemComponents = { ...stack.components, enchants: enchants.map((e) => ({ ...e })) };
  return { item: stack.item, count: stack.count, components };
}

/** Effective level of an enchant on a stack (0 if absent). */
export function enchantLevel(stack: ItemStack, id: string): number {
  return stack.components?.enchants?.find((e) => e.id === id)?.lvl ?? 0;
}

/** Whether adding `id` conflicts with the stack's existing enchants (exclusivity). */
export function conflicts(reg: EnchantRegistry, stack: ItemStack, id: string): boolean {
  const existing = stack.components?.enchants ?? [];
  const def = reg.byId.get(id);
  if (!def) return true;
  for (const e of existing) {
    if (e.id === id) continue;
    if (def.exclusiveWith.includes(e.id) || reg.byId.get(e.id)?.exclusiveWith.includes(id)) return true;
  }
  return false;
}
